/**
 * @fileoverview Disclaimer component that displays a blocking disclaimer screen
 * until the user accepts the terms. Shows on every page load/refresh.
 *
 * @module Disclaimer
 * @requires react
 * @requires ./config/disclaimerConfig
 * @requires ./Disclaimer.css
 */

import { useState } from 'react';
import { disclaimerConfig } from './disclaimerConfig.js';
import './Disclaimer.css';
/**
 * Disclaimer Component
 *
 * A full-screen overlay that blocks access to the application until the user
 * accepts the disclaimer terms. Displays on every application open or refresh.
 *
 * Features:
 * - Always visible on mount (every page load/refresh)
 * - Two-panel layout (left: logo/title, right: content/button)
 * - Handles accept button click to hide for current session
 * - Fully configurable via disclaimerConfig
 *
 * @component
 * @param {Object} props - Component props
 * @param {Function} [props.onAccept] - Callback function called when user accepts
 * @returns {JSX.Element|null} Disclaimer overlay or null if not visible
 *
 * @example
 * ```jsx
 * <Disclaimer onAccept={() => console.log('Accepted')} />
 * ```
 */

const Disclaimer = ({ onAccept }) => {
  // Always show disclaimer on mount (every page load/refresh)
  const [isVisible, setIsVisible] = useState(true);
  /**
   * Handles the accept button click
   * Hides the disclaimer for the current session (until next page load/refresh)
   * Calls optional onAccept callback if provided
   *
   * @function
   * @returns {void}
   */

  const handleAccept = () => {
    setIsVisible(false);
    if (onAccept) {
      onAccept();
    }
  };

  // Don't render if not visible
  if (!isVisible) {
    return null;
  }

  return (
    <div className="disclaimer-overlay">
      <div className="disclaimer-container">
        {/* Left Panel */}
        <div className="disclaimer-left-panel">
          <div className="disclaimer-left-content">
            {/* TDIS Logo Section */}
            {disclaimerConfig.tdisLogo && (
              <div className="disclaimer-logo-section disclaimer-tdis-section">
                <img
                  src={disclaimerConfig.tdisLogo}
                  alt="TDIS Logo"
                  className="disclaimer-tdis-logo"
                />
              </div>
            )}

            {/* Texas A&M University Logo Section */}
            {disclaimerConfig.tamuLogo && (
              <div className="disclaimer-logo-section disclaimer-tamu-section">
                <img
                  src={disclaimerConfig.tamuLogo}
                  alt="Texas A&M University Logo"
                  className="disclaimer-tamu-logo"
                />
              </div>
            )}

            {/* IDRT Logo Section */}
            {disclaimerConfig.idrtLogo && (
              <div className="disclaimer-logo-section disclaimer-idrt-section">
                <img
                  src={disclaimerConfig.idrtLogo}
                  alt="IDRT Logo"
                  className="disclaimer-idrt-logo"
                />
              </div>
            )}
            {/* Footer */}
            {disclaimerConfig.footerPoweredBy && (
              <div className="disclaimer-footer">
                <p className="disclaimer-footer-text">{disclaimerConfig.footerPoweredBy}</p>
              </div>
            )}
          </div>
        </div>

        {/* Right Panel */}
        <div className="disclaimer-right-panel">
          <div className="disclaimer-content">
            {/* Welcome Section */}
            {disclaimerConfig.welcomeText && (
              <p className="disclaimer-welcome-text">{disclaimerConfig.welcomeText}</p>
            )}

            {disclaimerConfig.welcomeTitle && (
              <h2 className="disclaimer-welcome-title">{disclaimerConfig.welcomeTitle}</h2>
            )}

            {/* Information Section */}
            {disclaimerConfig.informationText && (
              <p className="disclaimer-information-text">{disclaimerConfig.informationText}</p>
            )}

            {/* Disclaimer Section */}
            {disclaimerConfig.disclaimerTitle && (
              <h3 className="disclaimer-disclaimer-title">{disclaimerConfig.disclaimerTitle}</h3>
            )}
            {disclaimerConfig.disclaimerText && (
              <div className="disclaimer-disclaimer-text">
                {disclaimerConfig.disclaimerText.map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>
            )}
          </div>

          {/* Button Container */}
          <div className="disclaimer-button-container">
            <button onClick={handleAccept} className="disclaimer-accept-button">
              {disclaimerConfig.acceptButtonText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
export default Disclaimer;
